import React, { useState, useEffect } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { 
  Plus, 
  Calculator,
  Landmark,
  CheckCircle,
  Clock,
  FileCheck,
  ArrowUpCircle,
  ArrowDownCircle,
  Eye,
  ArrowLeft,
  Trash2,
  RotateCcw,
  FileSpreadsheet
} from 'lucide-react';
import { toast } from 'sonner';
import { fmtDate as fmtDateTz } from '../lib/datetime';
import { CashCountDialog, CloseCashCountDialog, ViewCashCountDialog, ReconciliationDialog, ViewReconciliationDialog, AddStatementItemDialog, DeleteControlDialog } from '../components/treasury/ControlDialogs';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const userCan = (user, module, action) => {
  if (!user) return false;
  if (user.role === 'admin') return true;
  return user.permissions?.[module]?.[action] ?? false;
};

function TreasuryControl() {
  const { user } = useOutletContext();
  const navigate = useNavigate();
  const getFirstTab = () => {
    if (userCan(user, 'tesoreria_arqueos', 'ver')) return 'cash-counts';
    if (userCan(user, 'tesoreria_conciliaciones', 'ver')) return 'reconciliations';
    return 'cash-counts';
  };
  const [activeTab, setActiveTab] = useState(getFirstTab());
  const [loading, setLoading] = useState(true);
  
  // Data
  const [cashCounts, setCashCounts] = useState([]);
  const [reconciliations, setReconciliations] = useState([]);
  const [cashBanks, setCashBanks] = useState([]);
  const [incomeConcepts, setIncomeConcepts] = useState([]);
  const [expenseConcepts, setExpenseConcepts] = useState([]);
  
  // Dialogs
  const [cashCountDialogOpen, setCashCountDialogOpen] = useState(false);
  const [closeCashCountDialogOpen, setCloseCashCountDialogOpen] = useState(false);
  const [closeSubmitting, setCloseSubmitting] = useState(false);
  const [cashCountAdjustments, setCashCountAdjustments] = useState([]);
  // v3.34.2 · Ver arqueo (modal readonly)
  const [viewCashCountDialogOpen, setViewCashCountDialogOpen] = useState(false);
  const [viewCashCountTarget, setViewCashCountTarget] = useState(null);
  const [viewCashCountAdjustments, setViewCashCountAdjustments] = useState([]);
  // v3.34.0 · Reabrir arqueo cerrado
  const [reopenCashCountDialogOpen, setReopenCashCountDialogOpen] = useState(false);
  const [reopenCashCountTarget, setReopenCashCountTarget] = useState(null);
  const [reopenCashCountReason, setReopenCashCountReason] = useState('');
  const [reopenCashCountSubmitting, setReopenCashCountSubmitting] = useState(false);
  // v3.33.2 · Reabrir conciliación completada
  const [reopenDialogOpen, setReopenDialogOpen] = useState(false);
  const [reopenTarget, setReopenTarget] = useState(null);
  const [reopenReason, setReopenReason] = useState('');
  const [reopenSubmitting, setReopenSubmitting] = useState(false);
  const [reconciliationDialogOpen, setReconciliationDialogOpen] = useState(false);
  const [viewReconciliationDialogOpen, setViewReconciliationDialogOpen] = useState(false);
  const [addStatementItemDialogOpen, setAddStatementItemDialogOpen] = useState(false);
  
  // Selected items
  const [selectedCashCount, setSelectedCashCount] = useState(null);
  const [selectedReconciliation, setSelectedReconciliation] = useState(null);
  
  // Forms
  const [cashCountForm, setCashCountForm] = useState({
    date: new Date().toISOString().split('T')[0],
    cash_bank_id: '',
    physical_balance: '',
    notes: ''
  });
  
  const [closeForm, setCloseForm] = useState({
    expense_concept_id: '',
    income_concept_id: ''
  });
  
  const [reconciliationForm, setReconciliationForm] = useState({
    date: new Date().toISOString().split('T')[0],
    cash_bank_id: '',
    period_start: '',
    period_end: '',
    bank_statement_balance: '',
    notes: ''
  });
  
  const [statementItemForm, setStatementItemForm] = useState({
    date: new Date().toISOString().split('T')[0],
    description: '',
    amount: '',
    concept_type: 'expense',
    concept_id: '',
    notes: ''
  });
  
  // Delete confirmation
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // { type: 'cash_count' | 'reconciliation', item: object }
  
  const confirmDelete = (type, item) => {
    setDeleteTarget({ type, item });
    setDeleteConfirmOpen(true);
  };
  
  const executeDelete = async () => {
    if (!deleteTarget) return;
    const { type, item } = deleteTarget;
    setDeleteConfirmOpen(false);
    setDeleteTarget(null);
    
    try {
      if (type === 'cash_count') {
        await axios.post(`${BACKEND_URL}/api/treasury/cash-counts/${item.cash_count_id}/delete`, {}, { withCredentials: true });
        toast.success(`Arqueo ${item.cash_count_number} eliminado`);
      } else if (type === 'reconciliation') {
        await axios.post(`${BACKEND_URL}/api/treasury/reconciliations/${item.reconciliation_id}/delete`, {}, { withCredentials: true });
        toast.success(`Conciliación ${item.reconciliation_number} eliminada`);
      }
      fetchAllData();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Error al eliminar';
      toast.error(msg);
    }
  };
  
  const canArqueo = userCan(user, 'tesoreria_arqueos', 'ver');
  const canCreateArqueo = userCan(user, 'tesoreria_arqueos', 'crear');
  const canCloseArqueo = userCan(user, 'tesoreria_arqueos', 'editar');
  const canReopenArqueo = userCan(user, 'tesoreria_arqueos', 'reabrir');
  const canConciliacion = userCan(user, 'tesoreria_conciliaciones', 'ver');
  const canCreateConciliacion = userCan(user, 'tesoreria_conciliaciones', 'crear');
  const canConciliar = userCan(user, 'tesoreria_conciliaciones', 'conciliar');
  
  useEffect(() => {
    fetchAllData();
  }, []);
  
  const fetchAllData = async () => {
    try {
      const safe = (p) => p.catch(() => ({ data: [] }));
      const [cashCountsRes, reconciliationsRes, cashBanksRes, incomeRes, expenseRes] = await Promise.all([
        safe(axios.get(`${BACKEND_URL}/api/treasury/cash-counts`, { withCredentials: true })),
        safe(axios.get(`${BACKEND_URL}/api/treasury/reconciliations`, { withCredentials: true })),
        safe(axios.get(`${BACKEND_URL}/api/treasury/cash-banks`, { withCredentials: true })),
        safe(axios.get(`${BACKEND_URL}/api/treasury/income-concepts/active`, { withCredentials: true })),
        safe(axios.get(`${BACKEND_URL}/api/treasury/expense-concepts/active`, { withCredentials: true }))
      ]);
      
      setCashCounts(cashCountsRes.data);
      setReconciliations(reconciliationsRes.data);
      setCashBanks(cashBanksRes.data);
      setIncomeConcepts(incomeRes.data);
      setExpenseConcepts(expenseRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };
  
  // ============ ARQUEO DE CAJAS ============
  const handleCreateCashCount = async () => {
    if (!cashCountForm.cash_bank_id || cashCountForm.physical_balance === '') {
      toast.error('Completa los campos obligatorios');
      return;
    }
    
    try {
      await axios.post(`${BACKEND_URL}/api/treasury/cash-counts`, {
        ...cashCountForm,
        physical_balance: parseFloat(cashCountForm.physical_balance)
      }, { withCredentials: true });
      
      toast.success('Arqueo creado');
      setCashCountDialogOpen(false);
      resetCashCountForm();
      fetchAllData();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Error al crear arqueo';
      if (msg.includes('arqueo confirmado')) {
        window.alert(msg);
      } else {
        toast.error(msg);
      }
    }
  };
  
  const resetCashCountForm = () => {
    setCashCountForm({
      date: new Date().toISOString().split('T')[0],
      cash_bank_id: '',
      physical_balance: '',
      notes: ''
    });
  };
  
  const handleOpenClose = async (cashCount) => {
    setSelectedCashCount(cashCount);
    setCloseForm({ expense_concept_id: '', income_concept_id: '' });
    // v3.34.0 — cargar ajustes previos del arqueo
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/treasury/cash-counts/${cashCount.cash_count_id}/adjustments`,
        { withCredentials: true });
      setCashCountAdjustments(res.data || []);
    } catch (e) {
      setCashCountAdjustments([]);
    }
    setCloseCashCountDialogOpen(true);
  };

  const refreshCashCountAdjustments = async (ccId) => {
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/treasury/cash-counts/${ccId}/adjustments`,
        { withCredentials: true });
      setCashCountAdjustments(res.data || []);
    } catch (e) {
      // noop
    }
  };

  const handleAddCashCountAdjustment = async (payload) => {
    if (!selectedCashCount) return;
    try {
      await axios.post(
        `${BACKEND_URL}/api/treasury/cash-counts/${selectedCashCount.cash_count_id}/adjustments`,
        payload, { withCredentials: true });
      toast.success('Ajuste agregado');
      await refreshCashCountAdjustments(selectedCashCount.cash_count_id);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No se pudo agregar el ajuste');
    }
  };

  const handleRemoveCashCountAdjustment = async (adjustmentId) => {
    if (!selectedCashCount) return;
    if (!window.confirm('¿Eliminar este ajuste? Se revertirán saldo, mayorización y asiento contable.')) return;
    try {
      await axios.post(
        `${BACKEND_URL}/api/treasury/cash-counts/${selectedCashCount.cash_count_id}/adjustments/${adjustmentId}/delete`,
        {}, { withCredentials: true });
      toast.success('Ajuste eliminado');
      await refreshCashCountAdjustments(selectedCashCount.cash_count_id);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No se pudo eliminar el ajuste');
    }
  };

  const handleCloseCashCount = async () => {
    if (!selectedCashCount) return;

    // Recalcular residual local
    const signed = (cashCountAdjustments || []).reduce((s, a) => {
      const amt = Number(a.amount || 0);
      return s + (a.adjustment_type === 'debit' ? amt : -amt);
    }, 0);
    const residual = Math.round((Number(selectedCashCount.difference || 0) - signed) * 100) / 100;

    if (residual > 0.009 && !closeForm.income_concept_id) {
      toast.error('Selecciona un concepto de ingreso para el residual sobrante');
      return;
    }
    if (residual < -0.009 && !closeForm.expense_concept_id) {
      toast.error('Selecciona un concepto de gasto para el residual faltante');
      return;
    }

    setCloseSubmitting(true);
    try {
      let url = `${BACKEND_URL}/api/treasury/cash-counts/${selectedCashCount.cash_count_id}/close?`;
      if (closeForm.expense_concept_id) url += `expense_concept_id=${closeForm.expense_concept_id}&`;
      if (closeForm.income_concept_id) url += `income_concept_id=${closeForm.income_concept_id}`;

      await axios.put(url, {}, { withCredentials: true });

      toast.success('Arqueo cerrado · bloqueo bancario aplicado');
      setCloseCashCountDialogOpen(false);
      setSelectedCashCount(null);
      setCashCountAdjustments([]);
      fetchAllData();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Error al cerrar arqueo';
      if (msg.includes('arqueo confirmado')) {
        window.alert(msg);
      } else {
        toast.error(msg);
      }
    } finally {
      setCloseSubmitting(false);
    }
  };

  // v3.34.2 — Ver arqueo (lectura: fetch cash_count + adjustments y abre dialog)
  const handleViewCashCount = async (cashCount) => {
    try {
      const [ccRes, adjRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/treasury/cash-counts/${cashCount.cash_count_id}`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/treasury/cash-counts/${cashCount.cash_count_id}/adjustments`, { withCredentials: true }),
      ]);
      setViewCashCountTarget(ccRes.data);
      setViewCashCountAdjustments(adjRes.data || []);
      setViewCashCountDialogOpen(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cargar el arqueo');
    }
  };

  // Funciones de delete reemplazadas por confirmDelete/executeDelete (Dialog)
  
  
  // v3.34.10 — Parse flexible: acepta "4.173.820,66" (AR), "4173820.66" (EN), negativos.
  const parseLocaleNumber = (val) => {
    if (val === null || val === undefined) return NaN;
    const s = String(val).trim();
    if (!s) return NaN;
    // Si tiene coma, asumir formato AR: quitar puntos (miles) y pasar coma a punto.
    // Si no tiene coma, asumir formato EN y dejar como está.
    const normalized = s.includes(',') ? s.replace(/\./g, '').replace(',', '.') : s;
    const n = parseFloat(normalized);
    return Number.isFinite(n) ? n : NaN;
  };

  // ============ CONCILIACIÓN BANCARIA ============
  const handleCreateReconciliation = async () => {
    const bankBalance = parseLocaleNumber(reconciliationForm.bank_statement_balance);
    if (!reconciliationForm.cash_bank_id || !reconciliationForm.period_start || 
        !reconciliationForm.period_end || Number.isNaN(bankBalance)) {
      toast.error('Completa los campos obligatorios');
      return;
    }
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/treasury/reconciliations`, {
        ...reconciliationForm,
        bank_statement_balance: bankBalance
      }, { withCredentials: true });
      
      toast.success('Conciliación creada');
      setReconciliationDialogOpen(false);
      resetReconciliationForm();
      fetchAllData();
      
      // Abrir para ver items
      setSelectedReconciliation(response.data);
      setViewReconciliationDialogOpen(true);
    } catch (error) {
      const msg = error.response?.data?.detail || 'Error al crear conciliación';
      if (msg.includes('conciliación completada')) {
        window.alert(msg);
      } else {
        toast.error(msg);
      }
    }
  };
  
  // Delete reconciliation replaced by confirmDelete/executeDelete
  
  const resetReconciliationForm = () => {
    setReconciliationForm({
      date: new Date().toISOString().split('T')[0],
      cash_bank_id: '',
      period_start: '',
      period_end: '',
      bank_statement_balance: '',
      notes: ''
    });
  };
  
  const handleViewReconciliation = async (reconciliation) => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/treasury/reconciliations/${reconciliation.reconciliation_id}`,
        { withCredentials: true }
      );
      setSelectedReconciliation(response.data);
      setViewReconciliationDialogOpen(true);
    } catch (error) {
      toast.error('Error al cargar conciliación');
    }
  };
  
  const handleAddStatementItem = async () => {
    if (!statementItemForm.description || !statementItemForm.amount || !statementItemForm.concept_id) {
      toast.error('Completa todos los campos obligatorios (concepto, descripción, monto)');
      return;
    }
    
    try {
      await axios.post(
        `${BACKEND_URL}/api/treasury/reconciliations/${selectedReconciliation.reconciliation_id}/items`,
        {
          date: statementItemForm.date,
          description: statementItemForm.description,
          amount: parseFloat(statementItemForm.amount),
          concept_type: statementItemForm.concept_type,
          concept_id: statementItemForm.concept_id,
          notes: statementItemForm.notes
        },
        { withCredentials: true }
      );
      
      toast.success('Movimiento bancario agregado al sistema');
      setAddStatementItemDialogOpen(false);
      setStatementItemForm({ date: new Date().toISOString().split('T')[0], description: '', amount: '', concept_type: 'expense', concept_id: '', notes: '' });
      
      // Recargar conciliación
      handleViewReconciliation(selectedReconciliation);
    } catch (error) {
      const msg = error.response?.data?.detail || 'Error al agregar movimiento';
      toast.error(msg);
    }
  };
  
  // v3.32.7 · Replica frontend de `update_reconciliation_totals` del backend. Permite
  // optimistic-update del checkbox sin esperar un refetch (que tardaba 800-2000ms y
  // bloqueaba la UI tras cada click).
  const recomputeReconciliationTotals = (rec) => {
    const items = rec?.items || [];
    const reconciled_credits = items.filter(i => i.is_reconciled && i.is_credit).reduce((s, i) => s + (i.amount || 0), 0);
    const reconciled_debits = items.filter(i => i.is_reconciled && !i.is_credit).reduce((s, i) => s + (i.amount || 0), 0);
    const pending_credits = items.filter(i => !i.is_reconciled && !i.is_in_transit && i.is_credit && i.item_type === 'system_movement').reduce((s, i) => s + (i.amount || 0), 0);
    const pending_debits = items.filter(i => !i.is_reconciled && !i.is_in_transit && !i.is_credit && i.item_type === 'system_movement').reduce((s, i) => s + (i.amount || 0), 0);
    const transit_credits = items.filter(i => i.is_in_transit && i.is_credit).reduce((s, i) => s + (i.amount || 0), 0);
    const transit_debits = items.filter(i => i.is_in_transit && !i.is_credit).reduce((s, i) => s + (i.amount || 0), 0);
    const system_balance = rec?.system_balance || 0;
    const expected_bank = system_balance - transit_credits + transit_debits;
    const difference = (rec?.bank_statement_balance || 0) - expected_bank;
    return {
      ...rec,
      reconciled_balance: reconciled_credits - reconciled_debits,
      pending_credits, pending_debits,
      transit_credits, transit_debits,
      difference,
    };
  };

  const handleToggleReconciled = async (item) => {
    // Snapshot para rollback
    const prev = selectedReconciliation;
    if (!prev) return;

    // v3.32.7 · Optimistic update: toggleamos el item localmente y recomputamos los
    // totales SIN esperar al backend. El click responde en < 16ms.
    const newIsReconciled = !item.is_reconciled;
    const nextItems = (prev.items || []).map(it =>
      it.item_id === item.item_id ? { ...it, is_reconciled: newIsReconciled } : it
    );
    const optimistic = recomputeReconciliationTotals({ ...prev, items: nextItems });
    setSelectedReconciliation(optimistic);

    try {
      await axios.put(
        `${BACKEND_URL}/api/treasury/reconciliations/${prev.reconciliation_id}/items/${item.item_id}/reconcile`,
        {
          movement_id: item.movement_id,
          item_type: item.item_type,
          movement_type: item.movement_type,
          movement_type_label: item.movement_type_label,
          date: item.date,
          description: item.description,
          amount: item.amount,
          debit: item.debit,
          credit: item.credit,
          is_credit: item.is_credit
        },
        { withCredentials: true }
      );
      // No recargamos — ya aplicamos el cambio localmente y los totales están correctos.
    } catch (error) {
      // Rollback si el backend falla
      setSelectedReconciliation(prev);
      toast.error('Error al conciliar · se revirtió el cambio');
    }
  };
  
  // v3.26.0 — Marcar/desmarcar item como diferencia transitoria
  const handleSetTransit = async (item, isInTransit, category, notes) => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/treasury/reconciliations/${selectedReconciliation.reconciliation_id}/items/${item.item_id}/transit`,
        {
          is_in_transit: isInTransit,
          transit_category: category || null,
          transit_notes: notes || null,
          movement: {
            movement_id: item.movement_id,
            item_type: item.item_type,
            movement_type: item.movement_type,
            movement_type_label: item.movement_type_label,
            date: item.date,
            description: item.description,
            amount: item.amount,
            debit: item.debit,
            credit: item.credit,
            is_credit: item.is_credit,
          }
        },
        { withCredentials: true }
      );
      handleViewReconciliation(selectedReconciliation);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al marcar en tránsito');
    }
  };
  
  // v3.34.1 — Eliminar ajuste manual cargado dentro de la conciliación (revierte saldo + asiento)
  const handleDeleteReconciliationItem = async (item) => {
    if (!selectedReconciliation) return;
    if (item.item_type !== 'reconciliation_adjustment') return;
    const label = `${item.concept_name || 'ajuste'} · ${formatCurrency(item.amount || 0)}`;
    if (!window.confirm(`¿Eliminar este ajuste manual (${label})?\n\nSe revertirán:\n • Saldo de la cuenta bancaria\n • Asiento contable en el Libro Diario\n\nNo afecta los movimientos del sistema (cobros, pagos, etc.).`)) return;
    try {
      await axios.post(
        `${BACKEND_URL}/api/treasury/reconciliations/${selectedReconciliation.reconciliation_id}/items/${item.item_id}/delete`,
        {},
        { withCredentials: true }
      );
      toast.success('Ajuste eliminado · saldo y asiento revertidos');
      handleViewReconciliation(selectedReconciliation);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar el ajuste');
    }
  };

  const handleCompleteReconciliation = async () => {
    if (selectedReconciliation && Math.abs(selectedReconciliation.difference) > 0.01) {
      window.alert(`No se puede completar la conciliación con diferencia de ${formatCurrency(selectedReconciliation.difference)}.\n\nAgregue los movimientos bancarios faltantes (gastos bancarios, intereses, etc.) o marque como "diferencia transitoria" los cheques emitidos no cobrados o depósitos en tránsito, para llevar la diferencia a $0.`);
      return;
    }
    if (!window.confirm('¿Completar esta conciliación? No podrá modificarse después.\n\nLas partidas marcadas como "En tránsito" se arrastrarán automáticamente a la próxima conciliación.')) return;
    
    try {
      await axios.put(
        `${BACKEND_URL}/api/treasury/reconciliations/${selectedReconciliation.reconciliation_id}/complete`,
        {},
        { withCredentials: true }
      );
      
      toast.success('Conciliación completada');
      setViewReconciliationDialogOpen(false);
      fetchAllData();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Error al completar';
      window.alert(msg);
    }
  };
  
  const formatCurrency = (amount, currency = 'ARS') => {
    return new Intl.NumberFormat('es-AR', { style: 'currency', currency, minimumFractionDigits: 2 }).format(amount);
  };
  
  const formatDate = (dateStr) => (dateStr ? fmtDateTz(dateStr) : '-');
  
  // Filtrar solo cajas físicas (arqueo) y bancos + tarjetas de crédito (conciliación)
  // v3.36.0 — Tarjetas de crédito también se concilian (contra el resumen de la tarjetera)
  const physicalCashes = cashBanks.filter(cb => cb.entity_type === 'cash' && cb.is_active);
  const bankAccounts = cashBanks.filter(cb => (cb.entity_type === 'bank' || cb.entity_type === 'credit_card') && cb.is_active);
  
  if (loading) {
    return <div className="flex items-center justify-center h-64"><p>Cargando...</p></div>;
  }
  
  return (
    <div className="space-y-6" data-testid="treasury-control-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/treasury')} data-testid="back-to-treasury-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-4xl font-bold tracking-tight">Control de Tesorería</h1>
            <p className="text-muted-foreground">Arqueo de cajas y conciliación bancaria</p>
          </div>
        </div>
      </div>
      
      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="flex w-full max-w-md">
          {canArqueo && <TabsTrigger value="cash-counts" className="flex items-center gap-2 flex-1">
            <Calculator className="w-4 h-4" />
            Arqueo de Cajas
          </TabsTrigger>}
          {canConciliacion && <TabsTrigger value="reconciliations" className="flex items-center gap-2 flex-1">
            <Landmark className="w-4 h-4" />
            Conciliación Bancaria
          </TabsTrigger>}
        </TabsList>
        
        {/* TAB ARQUEO DE CAJAS */}
        {canArqueo && <TabsContent value="cash-counts" className="space-y-4 mt-6">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <Calculator className="w-5 h-5 text-blue-500" />
              Arqueos de Caja
            </h2>
            {canCreateArqueo && (
              <Button onClick={() => setCashCountDialogOpen(true)} data-testid="new-cash-count-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Arqueo
              </Button>
            )}
          </div>
          
          {/* Cards de cajas físicas */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {physicalCashes.map(cb => (
              <Card key={cb.cash_bank_id}>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium">{cb.name}</span>
                    <Badge variant="outline">{cb.currency}</Badge>
                  </div>
                  <p className={`text-2xl font-bold ${cb.current_balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(cb.current_balance, cb.currency)}
                  </p>
                  <p className="text-xs text-muted-foreground">Saldo Teórico (Sistema)</p>
                </CardContent>
              </Card>
            ))}
            {physicalCashes.length === 0 && (
              <Card className="col-span-3">
                <CardContent className="pt-6 text-center text-muted-foreground">
                  No hay cajas físicas configuradas
                </CardContent>
              </Card>
            )}
          </div>
          
          <Card>
            <CardContent className="pt-6">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Número</TableHead>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Caja</TableHead>
                    <TableHead className="text-right">Saldo Teórico</TableHead>
                    <TableHead className="text-right">Saldo Físico</TableHead>
                    <TableHead className="text-right">Diferencia</TableHead>
                    <TableHead className="text-center">Estado</TableHead>
                    <TableHead className="text-center">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {cashCounts.map(cc => (
                    <TableRow key={cc.cash_count_id}>
                      <TableCell className="font-mono">{cc.cash_count_number}</TableCell>
                      <TableCell>{formatDate(cc.date)}</TableCell>
                      <TableCell>{cc.cash_bank_name}</TableCell>
                      <TableCell className="text-right">{formatCurrency(cc.theoretical_balance)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(cc.physical_balance)}</TableCell>
                      <TableCell className="text-right">
                        {cc.difference === 0 ? (
                          <span className="text-green-600">$0,00</span>
                        ) : cc.difference > 0 ? (
                          <span className="text-blue-600 flex items-center justify-end gap-1">
                            <ArrowUpCircle className="w-4 h-4" />
                            +{formatCurrency(cc.difference)}
                          </span>
                        ) : (
                          <span className="text-red-600 flex items-center justify-end gap-1">
                            <ArrowDownCircle className="w-4 h-4" />
                            {formatCurrency(cc.difference)}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {cc.status === 'open' ? (
                          <Badge className="bg-yellow-100 text-yellow-800">
                            <Clock className="w-3 h-3 mr-1" />Abierto
                          </Badge>
                        ) : (
                          <Badge className="bg-green-100 text-green-800">
                            <CheckCircle className="w-3 h-3 mr-1" />Cerrado
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex justify-center gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleViewCashCount(cc)}
                                  data-testid={`view-cash-count-${cc.cash_count_number}`}>
                            <Eye className="w-4 h-4 mr-1" />
                            Ver
                          </Button>
                          {cc.status === 'open' && canCloseArqueo && (
                            <>
                              <Button variant="outline" size="sm" onClick={() => handleOpenClose(cc)} data-testid={`cc-open-close-${cc.cash_count_number}`}>
                                <FileCheck className="w-4 h-4 mr-1" />
                                Cerrar
                              </Button>
                              {user?.role === 'admin' && (
                                <Button variant="outline" size="sm" className="text-red-500 hover:text-red-700" onClick={() => confirmDelete('cash_count', cc)} data-testid="delete-cash-count-btn">
                                  <Trash2 className="w-4 h-4 mr-1" />
                                  Eliminar
                                </Button>
                              )}
                            </>
                          )}
                          {cc.status === 'closed' && (user?.role === 'admin' || canReopenArqueo) && (
                            <Button variant="ghost" size="sm" className="text-amber-600 hover:text-amber-800"
                                    onClick={() => { setReopenCashCountTarget(cc); setReopenCashCountReason(''); setReopenCashCountDialogOpen(true); }}
                                    data-testid={`reopen-cash-count-${cc.cash_count_number}`}>
                              <RotateCcw className="w-4 h-4 mr-1" />
                              Reabrir
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {cashCounts.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                        No hay arqueos registrados
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>}
        
        {/* TAB CONCILIACIÓN BANCARIA */}
        {canConciliacion && <TabsContent value="reconciliations" className="space-y-4 mt-6">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <Landmark className="w-5 h-5 text-purple-500" />
              Conciliaciones Bancarias
            </h2>
            {canCreateConciliacion && (
              <Button onClick={() => setReconciliationDialogOpen(true)} data-testid="new-reconciliation-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nueva Conciliación
              </Button>
            )}
          </div>
          
          {/* Cards de cuentas bancarias */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {bankAccounts.map(cb => (
              <Card key={cb.cash_bank_id}>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium">{cb.name}</span>
                    <Badge variant="outline">{cb.currency}</Badge>
                  </div>
                  <p className={`text-2xl font-bold ${cb.current_balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(cb.current_balance, cb.currency)}
                  </p>
                  <p className="text-xs text-muted-foreground">Saldo según Sistema</p>
                </CardContent>
              </Card>
            ))}
            {bankAccounts.length === 0 && (
              <Card className="col-span-3">
                <CardContent className="pt-6 text-center text-muted-foreground">
                  No hay cuentas bancarias configuradas
                </CardContent>
              </Card>
            )}
          </div>
          
          <Card>
            <CardContent className="pt-6">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Número</TableHead>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Cuenta</TableHead>
                    <TableHead>Período</TableHead>
                    <TableHead className="text-right">Saldo Extracto</TableHead>
                    <TableHead className="text-right">Saldo Sistema</TableHead>
                    <TableHead className="text-right">Diferencia</TableHead>
                    <TableHead className="text-center">Estado</TableHead>
                    <TableHead className="text-center">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reconciliations.map(rec => (
                    <TableRow key={rec.reconciliation_id}>
                      <TableCell className="font-mono">{rec.reconciliation_number}</TableCell>
                      <TableCell>{formatDate(rec.date)}</TableCell>
                      <TableCell>{rec.cash_bank_name}</TableCell>
                      <TableCell className="text-sm">
                        {formatDate(rec.period_start)} - {formatDate(rec.period_end)}
                      </TableCell>
                      <TableCell className="text-right">{formatCurrency(rec.bank_statement_balance)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(rec.system_balance)}</TableCell>
                      <TableCell className="text-right">
                        <span className={rec.difference === 0 ? 'text-green-600' : 'text-orange-600'}>
                          {formatCurrency(rec.difference)}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        {rec.status === 'draft' ? (
                          <Badge className="bg-yellow-100 text-yellow-800">
                            <Clock className="w-3 h-3 mr-1" />Borrador
                          </Badge>
                        ) : (
                          <Badge className="bg-green-100 text-green-800">
                            <CheckCircle className="w-3 h-3 mr-1" />Completada
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex justify-center gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleViewReconciliation(rec)}>
                            <Eye className="w-4 h-4 mr-1" />
                            Ver
                          </Button>
                          {user?.role === 'admin' && rec.status === 'draft' && (
                            <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => confirmDelete('reconciliation', rec)} data-testid="delete-reconciliation-btn">
                              <Trash2 className="w-4 h-4 mr-1" />
                              Eliminar
                            </Button>
                          )}
                          {(user?.role === 'admin' || userCan(user, 'tesoreria_conciliaciones', 'reabrir')) && rec.status === 'completed' && (
                            <Button variant="ghost" size="sm" className="text-amber-600 hover:text-amber-800"
                                    onClick={() => { setReopenTarget(rec); setReopenReason(''); setReopenDialogOpen(true); }}
                                    data-testid={`reopen-reconciliation-${rec.reconciliation_number}`}>
                              <RotateCcw className="w-4 h-4 mr-1" />
                              Reabrir
                            </Button>
                          )}
                          {/* v3.36.13 · Export Excel del papel de trabajo */}
                          <Button variant="ghost" size="sm" className="text-emerald-700 hover:text-emerald-900"
                                  onClick={async () => {
                                    try {
                                      const r = await axios.get(
                                        `${BACKEND_URL}/api/treasury/reconciliations/${rec.reconciliation_id}/export-xlsx`,
                                        { withCredentials: true, responseType: 'blob' }
                                      );
                                      const url = window.URL.createObjectURL(new Blob([r.data]));
                                      const a = document.createElement('a');
                                      a.href = url; a.download = `conciliacion_${rec.reconciliation_number}.xlsx`;
                                      document.body.appendChild(a); a.click(); a.remove();
                                      window.URL.revokeObjectURL(url);
                                    } catch (e) {
                                      toast.error(e.response?.data?.detail || 'Error al exportar Excel');
                                    }
                                  }}
                                  data-testid={`export-xlsx-reconciliation-${rec.reconciliation_number}`}>
                            <FileSpreadsheet className="w-4 h-4 mr-1" />
                            Excel
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {reconciliations.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                        No hay conciliaciones registradas
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>}
      </Tabs>
      
      <CashCountDialog
        open={cashCountDialogOpen}
        onOpenChange={setCashCountDialogOpen}
        form={cashCountForm}
        setForm={setCashCountForm}
        physicalCashes={physicalCashes}
        onCancel={() => { setCashCountDialogOpen(false); resetCashCountForm(); }}
        onCreate={handleCreateCashCount}
      />

      <CloseCashCountDialog
        open={closeCashCountDialogOpen}
        onOpenChange={setCloseCashCountDialogOpen}
        cashCount={selectedCashCount}
        adjustments={cashCountAdjustments}
        closeForm={closeForm}
        setCloseForm={setCloseForm}
        incomeConcepts={incomeConcepts}
        expenseConcepts={expenseConcepts}
        onAddAdjustment={handleAddCashCountAdjustment}
        onRemoveAdjustment={handleRemoveCashCountAdjustment}
        onCancel={() => { setCloseCashCountDialogOpen(false); setCashCountAdjustments([]); }}
        onClose={handleCloseCashCount}
        submitting={closeSubmitting}
      />

      <ViewCashCountDialog
        open={viewCashCountDialogOpen}
        onOpenChange={setViewCashCountDialogOpen}
        cashCount={viewCashCountTarget}
        adjustments={viewCashCountAdjustments}
        onClose={() => { setViewCashCountDialogOpen(false); setViewCashCountTarget(null); setViewCashCountAdjustments([]); }}
      />

      <ReconciliationDialog
        open={reconciliationDialogOpen}
        onOpenChange={setReconciliationDialogOpen}
        form={reconciliationForm}
        setForm={setReconciliationForm}
        bankAccounts={bankAccounts}
        onCancel={() => { setReconciliationDialogOpen(false); resetReconciliationForm(); }}
        onCreate={handleCreateReconciliation}
      />

      <ViewReconciliationDialog
        open={viewReconciliationDialogOpen}
        onOpenChange={setViewReconciliationDialogOpen}
        reconciliation={selectedReconciliation}
        onAddItem={() => setAddStatementItemDialogOpen(true)}
        onToggleReconciled={handleToggleReconciled}
        onSetTransit={handleSetTransit}
        onDeleteItem={handleDeleteReconciliationItem}
        canReconcile={canConciliar}
        canComplete={canConciliar}
        onComplete={handleCompleteReconciliation}
        onClose={() => setViewReconciliationDialogOpen(false)}
      />

      <AddStatementItemDialog
        open={addStatementItemDialogOpen}
        onOpenChange={setAddStatementItemDialogOpen}
        form={statementItemForm}
        setForm={setStatementItemForm}
        incomeConcepts={incomeConcepts}
        expenseConcepts={expenseConcepts}
        onCancel={() => setAddStatementItemDialogOpen(false)}
        onAdd={handleAddStatementItem}
      />

      <DeleteControlDialog
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        target={deleteTarget}
        onCancel={() => setDeleteConfirmOpen(false)}
        onConfirm={executeDelete}
      />

      {/* v3.34.0 · Reabrir arqueo cerrado */}
      <Dialog open={reopenCashCountDialogOpen} onOpenChange={(v) => !reopenCashCountSubmitting && setReopenCashCountDialogOpen(v)}>
        <DialogContent className="max-w-md" data-testid="reopen-cash-count-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="w-5 h-5 text-amber-600" />
              Reabrir arqueo {reopenCashCountTarget?.cash_count_number}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-muted-foreground">
              Vuelve el arqueo a <b>abierto</b>, libera el <b>bloqueo bancario</b> de la caja/fecha y permite
              <b> agregar o eliminar ajustes</b>. Los ajustes ya cargados se mantienen (podés eliminarlos
              individualmente). Esta acción queda <b>auditada</b>.
            </p>
            <div className="rounded-md bg-amber-50 border border-amber-200 p-2 text-xs text-amber-900">
              Se bloquea si existe un arqueo cerrado posterior en la misma caja.
            </div>
            <div>
              <Label className="text-xs">Motivo (mín. 10 caracteres) *</Label>
              <Textarea rows={3} value={reopenCashCountReason}
                        onChange={(e) => setReopenCashCountReason(e.target.value)}
                        placeholder="Ej: identifiqué un ingreso que no se había cargado"
                        data-testid="reopen-cc-reason-input" />
              <p className="text-[11px] text-muted-foreground mt-1">{reopenCashCountReason.length}/10 caracteres</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReopenCashCountDialogOpen(false)} disabled={reopenCashCountSubmitting}>Cancelar</Button>
            <Button className="bg-amber-600 hover:bg-amber-700"
                    disabled={reopenCashCountSubmitting || reopenCashCountReason.trim().length < 10}
                    onClick={async () => {
                      setReopenCashCountSubmitting(true);
                      try {
                        const r = await axios.post(
                          `${BACKEND_URL}/api/treasury/cash-counts/${reopenCashCountTarget.cash_count_id}/reopen`,
                          { reason: reopenCashCountReason.trim() },
                          { withCredentials: true });
                        toast.success(`✓ Arqueo reabierto · ${r.data.bank_locks_removed} bloqueo(s) liberado(s)`);
                        setReopenCashCountDialogOpen(false);
                        setReopenCashCountTarget(null);
                        await fetchAllData();
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || 'No se pudo reabrir el arqueo');
                      } finally {
                        setReopenCashCountSubmitting(false);
                      }
                    }}
                    data-testid="reopen-cc-confirm-btn">
              {reopenCashCountSubmitting ? 'Reabriendo…' : 'Confirmar reapertura'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* v3.33.2 · Reabrir conciliación completada */}
      <Dialog open={reopenDialogOpen} onOpenChange={(v) => !reopenSubmitting && setReopenDialogOpen(v)}>
        <DialogContent className="max-w-md" data-testid="reopen-reconciliation-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="w-5 h-5 text-amber-600" />
              Reabrir conciliación {reopenTarget?.reconciliation_number}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-muted-foreground">
              Vuelve la conciliación al estado <b>borrador</b>, libera el <b>bloqueo bancario</b> del período y
              permite modificar los ítems conciliados. Esta acción queda <b>auditada</b>.
            </p>
            <div className="rounded-md bg-amber-50 border border-amber-200 p-2 text-xs text-amber-900">
              Los ítems ya conciliados conservan su marca <b>conciliado=sí/no</b>. Vas a poder destildar/retildar
              lo que necesites sin empezar de cero.
            </div>
            <div>
              <Label className="text-xs">Motivo (mín. 10 caracteres) *</Label>
              <Textarea rows={3} value={reopenReason}
                        onChange={(e) => setReopenReason(e.target.value)}
                        placeholder="Ej: faltó registrar un cobro del 28/09"
                        data-testid="reopen-reason-input" />
              <p className="text-[11px] text-muted-foreground mt-1">{reopenReason.length}/10 caracteres</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReopenDialogOpen(false)} disabled={reopenSubmitting}>Cancelar</Button>
            <Button className="bg-amber-600 hover:bg-amber-700"
                    disabled={reopenSubmitting || reopenReason.trim().length < 10}
                    onClick={async () => {
                      setReopenSubmitting(true);
                      try {
                        const r = await axios.post(
                          `${BACKEND_URL}/api/treasury/reconciliations/${reopenTarget.reconciliation_id}/reopen`,
                          { reason: reopenReason.trim() },
                          { withCredentials: true });
                        toast.success(`✓ Conciliación reabierta · ${r.data.bank_locks_removed} bloqueo(s) liberado(s)`);
                        setReopenDialogOpen(false);
                        setReopenTarget(null);
                        await fetchAllData();
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || 'No se pudo reabrir la conciliación');
                      } finally {
                        setReopenSubmitting(false);
                      }
                    }}
                    data-testid="reopen-confirm-btn">
              {reopenSubmitting ? 'Reabriendo…' : 'Confirmar reapertura'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}

export default TreasuryControl;
